import { useState, useEffect, useCallback, Component } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Cpu, RefreshCw, Loader2, Database, Wifi, Link2, ShieldCheck, Download, Play, PlugZap, LogOut } from 'lucide-react';

const API = '/api';

// ---------------------------------------------------------------------------
// Safe error/value rendering helpers — prevent React "Objects are not valid
// as a React child" crashes when backend returns structured error payloads.
// Also redacts token-like keys so secrets never reach the DOM.
// ---------------------------------------------------------------------------
const REDACT_KEYS = new Set([
  'access_token', 'refresh_token', 'client_secret', 'client_id', 'code',
  'authorization', 'bearer', 'token', 'secret', 'password',
]);

function redactDeep(value, depth = 0) {
  if (value === null || value === undefined) return value;
  if (depth > 6) return '[…depth]';
  if (Array.isArray(value)) return value.map((v) => redactDeep(v, depth + 1));
  if (typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (REDACT_KEYS.has(k.toLowerCase())) {
        out[k] = typeof v === 'string' && v.length > 0 ? `${v.slice(0, 4)}***` : '***';
      } else {
        out[k] = redactDeep(v, depth + 1);
      }
    }
    return out;
  }
  return value;
}

function formatHomeyError(err) {
  // axios-style: err.response.data.detail can be string OR object
  const detail = err?.response?.data?.detail ?? err?.response?.data ?? err?.message;
  if (!detail) return 'Onbekende fout';
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object') {
    const safe = redactDeep(detail);
    if (safe.message) return safe.code ? `[${safe.code}] ${safe.message}` : safe.message;
    if (safe.code) return safe.code;
    try { return JSON.stringify(safe).slice(0, 240); } catch { return 'Fout (object)'; }
  }
  return String(detail);
}

function formatDebugValue(value) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map((v) => typeof v === 'object' ? JSON.stringify(redactDeep(v)) : String(v)).join(', ');
  if (typeof value === 'object') {
    try { return JSON.stringify(redactDeep(value)); } catch { return '[object]'; }
  }
  return String(value);
}

// Minimal error boundary so a render crash in one panel does not blank the
// entire admin tab.
class HomeyDebugBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err) { /* eslint-disable-next-line no-console */ console.error('Homey debug panel error:', err?.message); }
  render() {
    if (this.state.err) {
      return (
        <div className="mt-3 border border-rose-200 bg-rose-50 rounded-lg p-3 text-[11px] text-rose-900" data-testid="dd-homey-debug-boundary">
          <strong>Debug panel kon niet gerenderd worden.</strong>
          <div className="mt-1">Foutmelding: {String(this.state.err?.message || this.state.err).slice(0, 200)}</div>
          <button onClick={() => { this.setState({ err: null }); if (this.props.onReset) this.props.onReset(); }} className="mt-2 text-[10px] text-rose-700 underline">Reset</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const PHASE_COLOR = {
  P0: 'bg-gray-50 text-gray-700 border-gray-200',
  P1: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  P2: 'bg-blue-50 text-blue-800 border-blue-200',
  P3: 'bg-amber-50 text-amber-800 border-amber-200',
  P4: 'bg-rose-50 text-rose-800 border-rose-200',
  research: 'bg-purple-50 text-purple-800 border-purple-200',
};

const SAFETY_COLOR = {
  read_only: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  limited_control_later: 'bg-blue-50 text-blue-800 border-blue-200',
  installer_required: 'bg-amber-50 text-amber-800 border-amber-200',
  unknown: 'bg-gray-50 text-gray-700 border-gray-200',
};

const SAFETY_LABEL = {
  read_only: 'Read-only',
  limited_control_later: 'Mogelijk later — opt-in',
  installer_required: 'Installateur',
  unknown: 'Onbekend',
};

export default function DeviceDiscoveryTab({ token }) {
  const [matrix, setMatrix] = useState(null);
  const [stats, setStats] = useState(null);
  const [homey, setHomey] = useState(null);
  const [readings, setReadings] = useState(null);
  const [homeyDevices, setHomeyDevices] = useState(null);
  const [homeyDebug, setHomeyDebug] = useState(null);
  const [patStatus, setPatStatus] = useState(null);
  const [patKey, setPatKey] = useState('');
  const [patHomeyId, setPatHomeyId] = useState('');
  const [patLabel, setPatLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  const authH = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, s, h, r, hd, pat] = await Promise.all([
        axios.get(`${API}/device-discovery/capability-matrix`),
        axios.get(`${API}/admin/device-discovery/matrix-stats`, { headers: authH }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/device-discovery/homey/oauth-status`, { headers: authH }),
        axios.get(`${API}/admin/device-discovery/homewizard/recent-readings?limit=10`, { headers: authH }),
        axios.get(`${API}/admin/device-discovery/homey/devices`, { headers: authH }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/device-discovery/homey/api-key/status`, { headers: authH }).catch(() => ({ data: null })),
      ]);
      setMatrix(m.data);
      setStats(s.data);
      setHomey(h.data);
      setReadings(r.data);
      setHomeyDevices(hd.data);
      setPatStatus(pat.data);
    } catch (e) {
      toast.error('Kon device discovery data niet laden');
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Detect Homey OAuth callback success/error in URL query
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('homey_connected') === '1') {
      toast.success('Homey verbonden! Klik "Fetch devices" om je apparaten op te halen.');
      const url = new URL(window.location.href);
      url.searchParams.delete('homey_connected');
      window.history.replaceState({}, '', url.toString());
    }
    const err = params.get('homey_error');
    if (err) {
      const msgMap = {
        token_exchange_301: 'Token endpoint redirect probleem — fix nodig in backend.',
        token_exchange_400: 'Athom weigerde token-exchange (HTTP 400). Check client credentials.',
        token_exchange_401: 'Client credentials zijn niet geldig (HTTP 401).',
        token_exchange_exception: 'Netwerkfout tijdens token-exchange. Probeer opnieuw.',
        token_missing: 'Athom gaf geen access_token terug. Probeer opnieuw.',
        state_invalid: 'OAuth state verlopen of ongeldig. Klik opnieuw "Connect Homey".',
        missing_code_or_state: 'Athom callback miste code of state.',
        authorize_denied: 'Autorisatie geweigerd op Athom. Klik opnieuw "Connect Homey" en accepteer de scopes.',
        forbidden_scope_granted: 'Athom verleende een control-scope die we niet willen. Verbinding geweigerd.',
        env_missing: 'Backend env vars ontbreken. Beheerder moet HOMEY_OAUTH_* keys invullen.',
      };
      toast.error(msgMap[err] || `Homey verbinding mislukt: ${err}`);
      const url = new URL(window.location.href);
      url.searchParams.delete('homey_error');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const seedMatrix = async (replace) => {
    if (replace && !window.confirm('Re-seed matrix? Dit overschrijft bestaande versie-rij.')) return;
    setBusy('seed');
    try {
      const { data } = await axios.post(
        `${API}/admin/device-discovery/seed-matrix${replace ? '?replace=true' : ''}`,
        {},
        { headers: authH },
      );
      if (data.success) toast.success(`Matrix geseed: ${data.seeded} producten`);
      else toast.info(data.reason);
      load();
    } catch (e) {
      toast.error(formatHomeyError(e) || 'Seed mislukt');
    }
    setBusy(null);
  };

  const startHomeyConnect = async () => {
    setBusy('homey-connect');
    try {
      const { data } = await axios.get(`${API}/admin/device-discovery/homey/begin-connect`, { headers: authH });
      if (data.authorize_url) {
        window.open(data.authorize_url, '_blank', 'noopener');
        toast.info('Open de Athom-pagina in het nieuwe tabblad om te autoriseren (read-only).');
      }
    } catch (e) {
      toast.error(formatHomeyError(e) || 'Homey OAuth nog niet geconfigureerd');
    }
    setBusy(null);
  };

  const fetchHomeyDevices = async (demo) => {
    setBusy(demo ? 'homey-demo' : 'homey-fetch');
    try {
      const { data } = await axios.post(
        `${API}/admin/device-discovery/homey/fetch-devices${demo ? '?demo=true' : ''}`,
        {},
        { headers: authH, validateStatus: (s) => s < 500 },
      );
      // Backend kan nu HTTP 200/400 met success=false geven (geen 5xx meer)
      if (data && data.success === false) {
        toast.error(`[${data.code || 'fetch_failed'}] ${data.message || 'Fetch mislukt'}`);
        // Vul debug panel met safe_details voor extra inspectie
        const sd = data.safe_details || {};
        const dbg = {
          session_present: true,
          error: { code: data.code, message: data.message, stage: data.stage },
          fetch_stage_debug: {
            stage: data.stage,
            delegation_debug: sd.delegation_debug,
            login_debug: sd.login_debug,
            remote_host: sd.remote_host,
          },
          discovery: sd.url_resolution
            ? { url_resolution: sd.url_resolution, homeys_with_url_inspection: [{ summary: sd.selected_homey, selected_homey_keys: sd.selected_homey_keys, url_flags: sd.url_resolution?.from_user_me?.flags || {}, url_candidates: sd.url_resolution?.from_user_me?.candidates || [], usable_remote_url_found: false }] }
            : (sd.attempts ? { attempts: sd.attempts, homeys: sd.homeys || [] } : null),
          safe_details: sd,
        };
        setHomeyDebug(dbg);
        return;
      }
      toast.success(`${data.device_count} devices gemapt (${data.source})`);
      setHomeyDevices(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(formatHomeyError(e) || 'Fetch mislukt');
      if (detail && typeof detail === 'object') {
        setHomeyDebug({
          session_present: true,
          error: { code: detail.code, message: detail.message },
          discovery: detail.discovery_summary || (detail.available ? { homeys: detail.available } : null),
        });
      }
    }
    setBusy(null);
  };

  const disconnectHomey = async () => {
    if (!window.confirm('Homey sessie loskoppelen? (data-snapshots blijven bewaard)')) return;
    setBusy('homey-disconnect');
    try {
      const { data } = await axios.post(`${API}/admin/device-discovery/homey/disconnect`, {}, { headers: authH });
      toast.success(`${data.disconnected_sessions} sessie(s) losgekoppeld`);
      setHomeyDebug(null);
      load();
    } catch (e) {
      toast.error(formatHomeyError(e) || 'Disconnect mislukt');
    }
    setBusy(null);
  };

  const runDebug = async () => {
    setBusy('homey-debug');
    try {
      const { data } = await axios.get(`${API}/admin/device-discovery/homey/debug`, { headers: authH });
      setHomeyDebug(data);
      if (!data.session_present) toast.info('Geen actieve sessie — klik eerst Connect Homey.');
      else toast.success(`Debug OK — ${data.discovery?.homeys?.length ?? 0} homey(s) gevonden`);
    } catch (e) {
      toast.error(formatHomeyError(e) || 'Debug call mislukt');
    }
    setBusy(null);
  };

  const savePat = async () => {
    if (!patKey || patKey.trim().length < 8) { toast.error('PAT moet ≥ 8 tekens zijn'); return; }
    setBusy('pat-save');
    try {
      const { data } = await axios.post(`${API}/admin/device-discovery/homey/api-key/save`, {
        api_key: patKey.trim(),
        homey_id: patHomeyId.trim() || null,
        label: patLabel.trim() || null,
      }, { headers: authH, validateStatus: (s) => s < 500 });
      if (data.success) {
        toast.success(`PAT opgeslagen (${data.api_key_prefix}). Klik 'Fetch met API Key'.`);
        setPatKey('');
        const { data: ns } = await axios.get(`${API}/admin/device-discovery/homey/api-key/status`, { headers: authH });
        setPatStatus(ns);
      } else {
        toast.error(`[${data.code}] ${data.message}`);
      }
    } catch (e) {
      toast.error(formatHomeyError(e) || 'PAT save mislukt');
    }
    setBusy(null);
  };

  const fetchPatDevices = async () => {
    setBusy('pat-fetch');
    try {
      const { data } = await axios.post(`${API}/admin/device-discovery/homey/api-key/fetch-devices`, {}, {
        headers: authH, validateStatus: (s) => s < 500,
      });
      if (data.success === false) {
        toast.error(`[${data.code}] ${data.message}`);
        setHomeyDebug({
          session_present: true,
          error: { code: data.code, message: data.message, stage: data.stage },
          fetch_stage_debug: { stage: data.stage, login_debug: data.safe_details?.login_debug },
        });
        return;
      }
      toast.success(`${data.device_count} devices gemapt via API Key (${data.source})`);
      setHomeyDevices(data);
    } catch (e) {
      toast.error(formatHomeyError(e) || 'PAT fetch mislukt');
    }
    setBusy(null);
  };

  const clearPat = async () => {
    if (!window.confirm('API Key pilot wissen? (snapshots blijven bewaard)')) return;
    setBusy('pat-clear');
    try {
      await axios.post(`${API}/admin/device-discovery/homey/api-key/clear`, {}, { headers: authH });
      toast.success('PAT pilot gewist');
      setPatStatus({ active: false });
    } catch (e) {
      toast.error(formatHomeyError(e) || 'Clear mislukt');
    }
    setBusy(null);
  };

  if (loading && !matrix) {
    return <div className="flex items-center justify-center py-12" data-testid="dd-tab-loading"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  }
  if (!matrix) return <div className="text-center py-8 text-sm text-muted-foreground">Geen data.</div>;

  const categoriesView = filterCategory === 'all' ? matrix.categories : matrix.categories.filter((c) => c.key === filterCategory);
  const mapped = homeyDevices?.devices || homeyDevices?.mapped_devices || [];
  const mappingSummary = homeyDevices?.summary || null;

  return (
    <div className="space-y-4" data-testid="device-discovery-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-base sm:text-lg font-medium flex items-center gap-2">
          <Cpu className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          Device Discovery (5A/5B/5C)
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">{matrix.version}</span>
        </h2>
        <button onClick={load} disabled={loading} className="px-2.5 py-1.5 text-xs border border-border rounded-md hover:bg-muted flex items-center gap-1.5" data-testid="dd-refresh">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-[11px] sm:text-xs text-blue-900" data-testid="dd-safety-banner">
        <strong>Read-only first.</strong> Geen device control. Geen flows. Geen schakelen. Geen klantfunnel impact. Scope: <code>homey.device.readonly</code>. Pilot-pagina: <code>/device-discovery</code>.
      </div>

      {/* Quick stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="dd-stats">
        <div className="bg-white border border-border rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Categorieën</div>
          <div className="text-xl font-medium">{matrix.categories.length}</div>
        </div>
        <div className="bg-white border border-border rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Producten in matrix</div>
          <div className="text-xl font-medium">{matrix.categories.reduce((a, c) => a + c.products.length, 0)}</div>
        </div>
        <div className="bg-white border border-border rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">DB seeded</div>
          <div className="text-xl font-medium">{stats?.total ?? '—'}</div>
        </div>
        <div className="bg-white border border-border rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">HW readings</div>
          <div className="text-xl font-medium">{readings?.total ?? 0}</div>
        </div>
      </div>

      {/* Seed DB action */}
      <div className="bg-white border border-border rounded-lg p-4 flex items-center justify-between flex-wrap gap-2" data-testid="dd-seed">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-primary" />
          <div>
            <div className="text-sm font-medium">Capability matrix → Mongo</div>
            <div className="text-[11px] text-muted-foreground">Idempotent. Schrijft `device_capability_matrix` collectie.</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => seedMatrix(false)} disabled={busy === 'seed'} className="px-3 py-1.5 text-xs btn-primary rounded-md flex items-center gap-1" data-testid="dd-seed-btn">
            {busy === 'seed' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            Seed
          </button>
          <button onClick={() => seedMatrix(true)} disabled={busy === 'seed'} className="px-3 py-1.5 text-xs border border-border rounded-md hover:bg-muted">
            Re-seed
          </button>
        </div>
      </div>

      {/* Homey OAuth status (5B Phase 1) */}
      <div className="bg-white border border-border rounded-lg p-4" data-testid="dd-homey-status">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-primary" />
            <div className="text-sm font-medium">Homey Pro — read-only discovery</div>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${homey?.ready ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
              {homey?.ready ? 'OAuth READY' : 'NIET GECONFIGUREERD'}
            </span>
            {homey?.demo_mode_active && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 font-medium">DEMO MODE</span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {homey?.ready && (
              <button onClick={startHomeyConnect} disabled={busy === 'homey-connect'} className="px-2.5 py-1.5 text-xs btn-primary rounded-md flex items-center gap-1" data-testid="dd-homey-connect">
                {busy === 'homey-connect' ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlugZap className="w-3 h-3" />}
                Connect Homey
              </button>
            )}
            <button onClick={() => fetchHomeyDevices(false)} disabled={busy === 'homey-fetch' || !homey?.ready} className="px-2.5 py-1.5 text-xs border border-border rounded-md hover:bg-muted flex items-center gap-1 disabled:opacity-40" data-testid="dd-homey-fetch">
              {busy === 'homey-fetch' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Fetch devices
            </button>
            <button onClick={() => fetchHomeyDevices(true)} disabled={busy === 'homey-demo'} className="px-2.5 py-1.5 text-xs border border-primary text-primary rounded-md hover:bg-primary/5 flex items-center gap-1" data-testid="dd-homey-demo">
              {busy === 'homey-demo' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              Demo mapping
            </button>
            <button onClick={runDebug} disabled={busy === 'homey-debug'} className="px-2.5 py-1.5 text-xs border border-amber-300 text-amber-800 rounded-md hover:bg-amber-50 flex items-center gap-1" data-testid="dd-homey-debug">
              {busy === 'homey-debug' ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
              Debug
            </button>
            {homeyDevices?.connected && (
              <button onClick={disconnectHomey} disabled={busy === 'homey-disconnect'} className="px-2.5 py-1.5 text-xs border border-rose-300 text-rose-700 rounded-md hover:bg-rose-50 flex items-center gap-1" data-testid="dd-homey-disconnect">
                <LogOut className="w-3 h-3" /> Disconnect
              </button>
            )}
          </div>
        </div>

        {!homey?.ready && (
          <div className="text-[11px] sm:text-xs text-muted-foreground space-y-1" data-testid="dd-homey-instructions">
            <div>Missende env: <code className="bg-muted px-1 rounded">{homey?.missing_env?.join(', ')}</code></div>
            <ol className="list-decimal pl-5 space-y-0.5 mt-1">
              <li>{homey?.instructions?.step_1}</li>
              <li>{homey?.instructions?.step_2}</li>
              <li>{homey?.instructions?.step_3}</li>
              <li>{homey?.instructions?.step_4}</li>
              <li>{homey?.instructions?.step_5}</li>
              <li className="italic">{homey?.instructions?.step_demo}</li>
            </ol>
            <p className="italic text-[11px] mt-2">Scope: <code>{homey?.scopes_requested?.join(', ')}</code> · {homey?.control_notice}</p>
          </div>
        )}

        {mappingSummary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid="dd-homey-summary">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700">Devices</div>
              <div className="text-lg font-medium text-emerald-900">{mappingSummary.total_devices}</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-2">
              <div className="text-[10px] uppercase tracking-wider text-blue-700">Met energie-data</div>
              <div className="text-lg font-medium text-blue-900">{mappingSummary.energy_relevant_devices}</div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded p-2">
              <div className="text-[10px] uppercase tracking-wider text-purple-700">Categorieën</div>
              <div className="text-lg font-medium text-purple-900">{Object.keys(mappingSummary.by_category || {}).length}</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-700">Source</div>
              <div className="text-sm font-medium text-gray-900">{homeyDevices?.source || '—'}</div>
            </div>
          </div>
        )}

        {mapped.length > 0 && (
          <div className="mt-3 overflow-x-auto" data-testid="dd-homey-mapped">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground bg-muted/40">
                <tr>
                  <th className="text-left px-2 py-1.5">Naam</th>
                  <th className="text-left px-2 py-1.5">Homey class</th>
                  <th className="text-left px-2 py-1.5">Powersure categorie</th>
                  <th className="text-left px-2 py-1.5">Safety</th>
                  <th className="text-left px-2 py-1.5 hidden sm:table-cell">Energie caps</th>
                </tr>
              </thead>
              <tbody>
                {mapped.map((d) => (
                  <tr key={d.homey_device_id} className="border-t border-border hover:bg-muted/20">
                    <td className="px-2 py-1 font-medium">{d.name}</td>
                    <td className="px-2 py-1 text-[10px] text-muted-foreground">{d.homey_class}</td>
                    <td className="px-2 py-1 text-[10px]">{d.powersure_label} <span className="text-muted-foreground">({d.powersure_category})</span></td>
                    <td className="px-2 py-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${SAFETY_COLOR[d.safety_class] || SAFETY_COLOR.unknown}`}>
                        {SAFETY_LABEL[d.safety_class] || d.safety_class}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-[10px] text-muted-foreground hidden sm:table-cell">{(d.energy_capabilities || []).join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] italic text-muted-foreground mt-2">Read-only mapping. Geen schakelen, geen flows, geen setpoint-wijzigingen.</p>
          </div>
        )}

        {homeyDebug && (
          <HomeyDebugBoundary onReset={() => setHomeyDebug(null)}>
            <div className="mt-3 border border-amber-200 bg-amber-50 rounded-lg p-3 text-[11px]" data-testid="dd-homey-debug-panel">
              <div className="flex items-center justify-between mb-1">
                <strong className="text-amber-900">Debug (admin-only, geen tokens/secrets)</strong>
                <button onClick={() => setHomeyDebug(null)} className="text-[10px] text-amber-700 hover:underline" data-testid="dd-homey-debug-close">sluiten</button>
              </div>
              <div className="space-y-1 text-amber-900">
                <div>Sessie aanwezig: <strong>{formatDebugValue(homeyDebug.session_present)}</strong></div>
                {homeyDebug.error && (
                  <div className="bg-rose-100 border border-rose-200 rounded p-2 mt-1" data-testid="dd-homey-debug-error">
                    <strong className="text-rose-900">Fout van backend:</strong>
                    <div className="text-rose-900 mt-0.5">[{formatDebugValue(homeyDebug.error.code)}] {formatDebugValue(homeyDebug.error.message)}</div>
                  </div>
                )}
                {homeyDebug.session && (
                  <div>
                    Scope: <code className="bg-white px-1 rounded">{formatDebugValue(homeyDebug.session.scope) || '—'}</code> ·
                    token-prefix: <code className="bg-white px-1 rounded">{formatDebugValue(homeyDebug.session.access_token_prefix)}</code> ·
                    refresh: {formatDebugValue(homeyDebug.session.has_refresh_token)} ·
                    expires_in: {formatDebugValue(homeyDebug.session.expires_in)}
                  </div>
                )}
                {homeyDebug.fetch_stage_debug && (homeyDebug.fetch_stage_debug.delegation_debug || homeyDebug.fetch_stage_debug.login_debug) && (
                  <div className="mt-2 bg-white border border-amber-300 rounded p-2 text-[10px]" data-testid="dd-homey-stage-debug">
                    <strong className="text-amber-900">Fetch-flow stage debug (geen tokens)</strong>
                    <div className="mt-1">Faalde bij stage: <code className="bg-amber-50 px-1 rounded">{formatDebugValue(homeyDebug.fetch_stage_debug.stage)}</code></div>
                    {homeyDebug.fetch_stage_debug.remote_host && (
                      <div>Remote host: <code className="bg-amber-50 px-1 rounded">{formatDebugValue(homeyDebug.fetch_stage_debug.remote_host)}</code></div>
                    )}
                    {homeyDebug.fetch_stage_debug.delegation_debug && (
                      <div className="mt-1 pl-2 border-l-2 border-amber-200">
                        <strong>1. Athom delegation /token:</strong>
                        <div>host={formatDebugValue(homeyDebug.fetch_stage_debug.delegation_debug.endpoint_host)} · path={formatDebugValue(homeyDebug.fetch_stage_debug.delegation_debug.path)} · status={formatDebugValue(homeyDebug.fetch_stage_debug.delegation_debug.status)} · ct={formatDebugValue(homeyDebug.fetch_stage_debug.delegation_debug.content_type)} · {formatDebugValue(homeyDebug.fetch_stage_debug.delegation_debug.elapsed_ms)}ms</div>
                        {homeyDebug.fetch_stage_debug.delegation_debug.top_level_keys && (
                          <div>response_keys: <code className="bg-amber-50 px-1 rounded">{(homeyDebug.fetch_stage_debug.delegation_debug.top_level_keys || []).join(',')}</code></div>
                        )}
                        {homeyDebug.fetch_stage_debug.delegation_debug.error_field && (
                          <div>error: <code className="bg-rose-50 px-1 rounded">{formatDebugValue(homeyDebug.fetch_stage_debug.delegation_debug.error_field)}</code></div>
                        )}
                      </div>
                    )}
                    {homeyDebug.fetch_stage_debug.login_debug && (
                      <div className="mt-1 pl-2 border-l-2 border-amber-200">
                        <strong>2. Homey {`{remoteUrl}/api/manager/users/login`}:</strong>
                        <div>host={formatDebugValue(homeyDebug.fetch_stage_debug.login_debug.endpoint_host)} · method={formatDebugValue(homeyDebug.fetch_stage_debug.login_debug.method)} · status={formatDebugValue(homeyDebug.fetch_stage_debug.login_debug.status)} · ct={formatDebugValue(homeyDebug.fetch_stage_debug.login_debug.content_type)} · {formatDebugValue(homeyDebug.fetch_stage_debug.login_debug.elapsed_ms)}ms</div>
                        <div>body_keys verzonden: <code className="bg-amber-50 px-1 rounded">{(homeyDebug.fetch_stage_debug.login_debug.body_keys || []).join(',')}</code></div>
                        {homeyDebug.fetch_stage_debug.login_debug.top_level_keys && (
                          <div>response_keys: <code className="bg-amber-50 px-1 rounded">{(homeyDebug.fetch_stage_debug.login_debug.top_level_keys || []).join(',')}</code></div>
                        )}
                        {homeyDebug.fetch_stage_debug.login_debug.body_shape === 'html' && (
                          <div className="text-rose-700">Body shape: HTML (mogelijk Cloudflare/proxy)</div>
                        )}
                        {homeyDebug.fetch_stage_debug.login_debug.body_preview && (
                          <div>body preview: <code className="bg-rose-50 px-1 rounded">{formatDebugValue(homeyDebug.fetch_stage_debug.login_debug.body_preview)}</code></div>
                        )}
                        {homeyDebug.fetch_stage_debug.login_debug.error_field && (
                          <div>error: <code className="bg-rose-50 px-1 rounded">{formatDebugValue(homeyDebug.fetch_stage_debug.login_debug.error_field)}</code></div>
                        )}
                        {homeyDebug.fetch_stage_debug.login_debug.timeout && (
                          <div className="text-rose-700">timeout (10s)</div>
                        )}
                        {homeyDebug.fetch_stage_debug.login_debug.exception && (
                          <div>exception: <code className="bg-rose-50 px-1 rounded">{formatDebugValue(homeyDebug.fetch_stage_debug.login_debug.exception)}</code></div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {homeyDebug.discovery && (
                  <>
                    <div className="mt-1">Endpoint pogingen:</div>
                    <ul className="list-disc pl-5 space-y-0.5">
                      {Array.isArray(homeyDebug.discovery.attempts) && homeyDebug.discovery.attempts.map((a, i) => (
                        <li key={i}>
                          <code className="text-[10px]">{formatDebugValue(a.url)}</code> → status <strong>{formatDebugValue(a.status ?? a.exception ?? 'err')}</strong>
                          {a.content_type ? <span className="text-amber-700"> · ct={formatDebugValue(a.content_type)}</span> : null}
                          {a.top_level_keys ? <span className="text-amber-700"> · keys={Array.isArray(a.top_level_keys) ? a.top_level_keys.join(',') : formatDebugValue(a.top_level_keys)}</span> : null}
                          {typeof a.total_devices_count === 'number' ? <span className="text-amber-700"> · total={a.total_devices_count}</span> : null}
                          {typeof a.accepted_homey_candidates_count === 'number' ? <span className="text-emerald-700"> · accepted={a.accepted_homey_candidates_count}</span> : null}
                          {typeof a.rejected_devices_count === 'number' && a.rejected_devices_count > 0 ? <span className="text-rose-700"> · rejected={a.rejected_devices_count}</span> : null}
                          {Array.isArray(a.rejected_sample) && a.rejected_sample.length > 0 && (
                            <ul className="list-disc pl-5 mt-0.5 text-rose-700 text-[10px]">
                              {a.rejected_sample.map((rs, j) => (
                                <li key={j}>id={formatDebugValue(rs.id_prefix)} · type={formatDebugValue(rs.type) || '—'} · platform={formatDebugValue(rs.platform) || '—'} · model={formatDebugValue(rs.model) || '—'} · reason=<code className="bg-white px-1 rounded">{formatDebugValue(rs.reason)}</code></li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                    {Array.isArray(homeyDebug.discovery.homeys) && homeyDebug.discovery.homeys.length > 0 && (
                      <div className="mt-2">Homeys gevonden ({homeyDebug.discovery.homeys.length}):
                        <ul className="list-disc pl-5">
                          {homeyDebug.discovery.homeys.map((h, i) => (
                            <li key={i}>
                              {formatDebugValue(h?.name) || '(zonder naam)'} ·
                              {' '}{formatDebugValue(h?.model || h?.platform) || '—'} ·
                              {' '}id={formatDebugValue(h?.id_prefix)} ·
                              {' '}online={formatDebugValue(h?.online)} ·
                              {' '}role={formatDebugValue(h?.role) || '—'} ·
                              {' '}parsed_from={formatDebugValue(h?.parsed_from)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {Array.isArray(homeyDebug.discovery.homeys_with_url_inspection) && homeyDebug.discovery.homeys_with_url_inspection.length > 0 && (
                      <div className="mt-2">
                        <strong>URL inspectie per Homey:</strong>
                        {homeyDebug.discovery.homeys_with_url_inspection.map((row, i) => (
                          <div key={i} className="mt-1 pl-2 border-l-2 border-amber-300">
                            <div>Naam: {formatDebugValue(row.summary?.name)} · id={formatDebugValue(row.summary?.id_prefix)} · remote bruikbaar: <strong>{formatDebugValue(row.usable_remote_url_found)}</strong></div>
                            <div className="text-[10px] mt-0.5">Aanwezige velden:
                              {row.url_flags && Object.entries(row.url_flags).filter(([, v]) => v).map(([k]) => (
                                <code key={k} className="bg-white px-1 ml-1 rounded">{k.replace('has_', '')}</code>
                              ))}
                              {row.url_flags && Object.entries(row.url_flags).every(([, v]) => !v) && <span className="italic text-amber-700"> geen URL-velden aanwezig</span>}
                            </div>
                            <div className="text-[10px] mt-0.5">Top-level keys: <code className="bg-white px-1 rounded">{(row.selected_homey_keys || []).join(', ')}</code></div>
                            {Array.isArray(row.url_candidates) && row.url_candidates.length > 0 && (
                              <ul className="list-disc pl-5 text-[10px]">
                                {row.url_candidates.map((c, j) => (
                                  <li key={j}><strong>{c.field}</strong>: <code className="bg-white px-1 rounded">{c.url_prefix}</code> {c.is_remote_usable ? <span className="text-emerald-700">✓ remote</span> : <span className="text-rose-700">✗ LAN/lokaal</span>}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {homeyDebug.discovery.homeys && homeyDebug.discovery.homeys.length === 0 && (
                      <div className="mt-1 italic text-amber-700">Geen homeys gevonden in welke endpoint dan ook. Controleer of dit Athom-account owner/admin is van een Homey Pro.</div>
                    )}
                  </>
                )}
                {/* Fallback raw JSON for anything we did not explicitly render */}
                <details className="mt-2">
                  <summary className="cursor-pointer text-amber-700 text-[10px]">Toon volledige debug (veilig, geredacteerd)</summary>
                  <pre className="text-[10px] bg-white p-2 rounded mt-1 overflow-x-auto max-h-64">{JSON.stringify(redactDeep(homeyDebug), null, 2)}</pre>
                </details>
              </div>
            </div>
          </HomeyDebugBoundary>
        )}
      </div>

      {/* Homey PAT pilot fallback (5B Phase 1B) */}
      <div className="bg-white border border-border rounded-lg p-4" data-testid="dd-homey-pat">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-amber-700" />
            <div className="text-sm font-medium">Homey — API Key (PAT) pilot</div>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${patStatus?.active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>
              {patStatus?.active ? 'ACTIEF' : 'NIET GECONFIGUREERD'}
            </span>
          </div>
          {patStatus?.active && (
            <button onClick={clearPat} disabled={busy === 'pat-clear'} className="px-2.5 py-1.5 text-xs border border-rose-300 text-rose-700 rounded-md hover:bg-rose-50" data-testid="dd-pat-clear">
              Clear pilot
            </button>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground mb-2">
          <strong>OAuth kon de Homey niet vinden via Athom /user/me</strong> (account-mobile devices only). Deze API Key route is een pilot fallback voor eigenaarstest — read-only, geen control, geen flows.
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-900 mb-2" data-testid="dd-pat-research-note">
          <strong>⚠️ Research-update feb 2026:</strong> Athom blokkeert REST-toegang voor zowel OAuth als PAT voor dit account-type. Direct REST naar <code>.connect.athom.com/api/</code> geeft <code>id: Invalid value</code> (vereist <code>homey-api</code> JS SDK met session-handshake, geen REST). PAT op Athom-cloud → 401 <code>invalid_token</code>. Zie <code>/app/memory/homey_remote_access_research.md</code>. Aanbevolen pivot: <strong>browser-bridge</strong> (zoals HomeWizard P1) of <strong>local bridge agent</strong>.
        </div>
        <ol className="text-[11px] text-muted-foreground list-decimal pl-5 mb-2 space-y-0.5">
          <li>Ga naar <a href="https://my.homey.app/me" target="_blank" rel="noopener" className="text-primary underline">https://my.homey.app/me</a> → log in</li>
          <li>Settings → "API Keys" → "Create Personal Access Token" → kies scope <code>devices.readonly</code> + <code>energy.readonly</code></li>
          <li>Kopieer de PAT en het Homey-id (te zien in de URL of in Settings)</li>
          <li>Plak hieronder en klik Save → Fetch met API Key</li>
        </ol>
        {patStatus?.active ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-[11px] flex items-center justify-between flex-wrap gap-2">
            <div>
              Actief sinds {patStatus.created_at} · host=<code className="bg-white px-1 rounded">{patStatus.remote_url_host}</code> · key=<code className="bg-white px-1 rounded">{patStatus.api_key_prefix}</code> · label=<code className="bg-white px-1 rounded">{patStatus.label || '—'}</code>
            </div>
            <button onClick={fetchPatDevices} disabled={busy === 'pat-fetch'} className="px-2.5 py-1.5 text-xs btn-primary rounded-md flex items-center gap-1" data-testid="dd-pat-fetch">
              {busy === 'pat-fetch' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              Fetch met API Key
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input type="password" value={patKey} onChange={(e) => setPatKey(e.target.value)} placeholder="PAT (Personal Access Token)" className="text-xs border border-border rounded px-2 py-1.5" data-testid="dd-pat-key-input" />
            <input type="text" value={patHomeyId} onChange={(e) => setPatHomeyId(e.target.value)} placeholder="Homey-id (alfanumeriek)" className="text-xs border border-border rounded px-2 py-1.5" data-testid="dd-pat-homeyid-input" />
            <input type="text" value={patLabel} onChange={(e) => setPatLabel(e.target.value)} placeholder="Label (optioneel)" className="text-xs border border-border rounded px-2 py-1.5" data-testid="dd-pat-label-input" />
            <button onClick={savePat} disabled={busy === 'pat-save'} className="sm:col-span-3 px-3 py-1.5 text-xs btn-primary rounded-md flex items-center justify-center gap-1" data-testid="dd-pat-save">
              {busy === 'pat-save' ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
              Save API Key (read-only)
            </button>
          </div>
        )}
        <div className="text-[10px] italic text-muted-foreground mt-2">
          API key wordt server-side opgeslagen (admin-only collection), nooit teruggegeven via API. Geen control. Geen flows. Geen setCapabilityValue.
        </div>
      </div>

      {/* Recent HomeWizard readings (5A) */}
      <div className="bg-white border border-border rounded-lg p-4" data-testid="dd-readings">
        <div className="flex items-center gap-2 mb-2">
          <Wifi className="w-4 h-4 text-primary" />
          <div className="text-sm font-medium">Recente HomeWizard P1 readings (pilot)</div>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{readings?.total ?? 0} totaal</span>
        </div>
        {readings?.recent?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground">
                <tr><th className="text-left px-1 py-1">Tijdstip</th><th className="text-left px-1">W</th><th className="text-left px-1">Import kWh</th><th className="text-left px-1">Export kWh</th></tr>
              </thead>
              <tbody>
                {readings.recent.map((r) => (
                  <tr key={r.reading_id} className="border-t border-border">
                    <td className="px-1 py-1">{new Date(r.received_at).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="px-1">{r.active_power_w ?? '—'}</td>
                    <td className="px-1">{r.total_power_import_kwh ?? '—'}</td>
                    <td className="px-1">{r.total_power_export_kwh ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Nog geen readings. Pilot test: open <code>/device-discovery</code> in de browser.</p>
        )}
      </div>

      {/* Filter chips */}
      <div className="flex gap-1.5 flex-wrap" data-testid="dd-category-filter">
        <button onClick={() => setFilterCategory('all')} className={`px-2 py-1 rounded-full text-[11px] border ${filterCategory === 'all' ? 'bg-primary text-white border-primary' : 'bg-white border-border'}`}>Alle</button>
        {matrix.categories.map((c) => (
          <button key={c.key} onClick={() => setFilterCategory(c.key)} className={`px-2 py-1 rounded-full text-[11px] border ${filterCategory === c.key ? 'bg-primary text-white border-primary' : 'bg-white border-border'}`}>
            {c.label.split('/')[0].trim()}
          </button>
        ))}
      </div>

      {/* Matrix table */}
      <div className="bg-white border border-border rounded-lg overflow-hidden" data-testid="dd-matrix">
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1.5">Product</th>
                <th className="text-left px-2 py-1.5">Categorie</th>
                <th className="text-left px-2 py-1.5">Phase</th>
                <th className="text-left px-2 py-1.5">Connection</th>
                <th className="text-left px-2 py-1.5 hidden sm:table-cell">Recommendation</th>
              </tr>
            </thead>
            <tbody>
              {categoriesView.flatMap((cat) =>
                cat.products.map((p, i) => (
                  <tr key={`${cat.key}-${i}`} className="border-t border-border hover:bg-muted/20">
                    <td className="px-2 py-1 font-medium">{p.name}</td>
                    <td className="px-2 py-1 text-muted-foreground">{cat.label.split('/')[0].trim()}</td>
                    <td className="px-2 py-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PHASE_COLOR[p.phase?.split('_')[0]] || PHASE_COLOR.research}`}>{p.phase || '—'}</span>
                    </td>
                    <td className="px-2 py-1 text-[10px] text-muted-foreground">{(p.connection || []).join(', ')}</td>
                    <td className="px-2 py-1 text-[10px] text-muted-foreground hidden sm:table-cell">{p.recommendation || '—'}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[10px] text-muted-foreground italic flex items-center gap-1">
        <ShieldCheck className="w-3 h-3" /> 5A/5B/5C read-only first. Geen controls geëxposeerd. Geen klantfunnel impact.
      </div>
    </div>
  );
}
